import token from 'api/definitions/tokenClient';
import { getSessionCookie } from 'redux/modules/page/selectors';
import { asyncLocalStorage } from 'server/utils/async-local-storage';
import { usingStubs } from 'stubs';
import { hashedCustomerId } from 'utils/crypto/hashedCustomerId';
import { getShoppingContext } from './get-shopping-context';
import { setAccessToken } from './set-access-token';
import { setHashedCustomerId } from './set-hashed-customer-id';
import { csrfTokenActionTypes, tokenActionTypes } from './types';

const getCsrfToken = () => ({
  types: csrfTokenActionTypes.triplet,
  apiCall: token.csrf(),
});

const getToken = (csrfToken: string, headerName: string, state: WtrState) => {
  /*
  The session cookie returned from the preceding CSRF call is not passed through
  on the server so adding it here explicitly (the api client handles only adding
  the cookie when on the server to avoid client-side errors).
  */
  const Cookie = getSessionCookie(state);

  return {
    types: tokenActionTypes.triplet,
    apiCall: token.token({
      headers: {
        [headerName]: csrfToken,
        Cookie,
      },
      iHandleStatusCodes: [401, 403],
    }),
  };
};

let tokenPromise: Promise<unknown> | null;

const initiateSessionCall = async (dispatch: WtrDispatch, getState: () => WtrState) => {
  const { headerName, token: csrfToken } = await dispatch<{ headerName: string; token: string }>(
    getCsrfToken(),
  );
  const { accessToken } = await dispatch<{ accessToken: string; customerId: string }>(
    getToken(csrfToken, headerName, getState()),
  );
  dispatch(setAccessToken(accessToken));
  const response = (await dispatch(getShoppingContext())) as {
    data?: { shoppingContext?: { customerId: string } };
  };
  const customerIdHashed = await hashedCustomerId(response?.data?.shoppingContext?.customerId);
  dispatch(setHashedCustomerId(customerIdHashed));
  return response;
};

export const initiateSession = () => (dispatch: WtrDispatch, getState: () => WtrState) => {
  if (__SERVER__) {
    const requestContext = asyncLocalStorage.getStore();
    const hasTokenSession = requestContext.get('hasTokenSession');

    return hasTokenSession || usingStubs()
      ? initiateSessionCall(dispatch, getState).catch(() => {})
      : Promise.resolve();
  }

  if (!tokenPromise) {
    // eslint-disable-next-line no-underscore-dangle
    const hasTokenSession = window.__WTR_WEBSITE__?.hasTokenSession;

    tokenPromise =
      hasTokenSession || usingStubs() ? initiateSessionCall(dispatch, getState) : Promise.reject();

    tokenPromise
      .catch(() => {})
      .finally(() => {
        tokenPromise = null;
      });
  }

  return tokenPromise;
};
